import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Check,
} from '@mui/icons-material';
import moment from 'moment';


// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Plan {
  id: string;
  name: string;
  price: number;
  features: string[];
}

interface CancelSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, feedback: string) => void;
  currentPlan: any;
  subscription: any;
}

export default function CancelSubscriptionModal({
  open,
  onClose,
  onConfirm,
  currentPlan,
  subscription
}: CancelSubscriptionModalProps) {
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');

  const handleConfirm = () => {
    onConfirm(cancelReason, cancelFeedback);
    setCancelReason('');
    setCancelFeedback('');
  };

  const handleClose = () => {
    setCancelReason('');
    setCancelFeedback('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'error.main' }}>
            <CancelIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Cancel Subscription
            </Typography>
            <Typography variant="caption" color="text.secondary">
              We're sorry to see you go
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Your subscription will be cancelled
          </Typography>
          <Typography variant="caption">
            You'll keep access until {moment(subscription.paidThroughDate).format('MMMM D, YYYY')}. After that, your account will be downgraded to the free tier if eligible.
          </Typography>
        </Alert>

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          You'll lose access to:
        </Typography>
        <List dense sx={{ mb: 2 }}>
          {currentPlan?.features?.map((feature: any, idx: number) => (
            <ListItem key={idx}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <WarningIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Before you go, can you tell us why? (optional)
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Reason for cancelling</InputLabel>
          <Select
            value={cancelReason}
            label="Reason for cancelling"
            onChange={(e) => setCancelReason(e.target.value)}
          >
            <MenuItem value="too-expensive">Too expensive</MenuItem>
            <MenuItem value="missing-features">Missing features I need</MenuItem>
            <MenuItem value="not-using">Not using it enough</MenuItem>
            <MenuItem value="switching">Switching to another service</MenuItem>
            <MenuItem value="technical">Technical issues</MenuItem>
            <MenuItem value="seasonal">Seasonal business</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Additional feedback (optional)"
          placeholder="Help us improve..."
          value={cancelFeedback}
          onChange={(e) => setCancelFeedback(e.target.value)}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button onClick={handleClose} variant="contained" color="primary" startIcon={<Check />}>
          Keep Subscription
        </Button>
        <Button
          onClick={handleConfirm}
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
        >
          Cancel Subscription
        </Button>
      </DialogActions>
    </Dialog>
  );
}